import { type ComputedRef, computed } from 'vue'
import { useUserStore } from '@/modules/user/stores/userStore'
import { getCurrentMatrixUserId } from '@/modules/chat-matrix-fast/services/matrixChatClient'
import type {
  IChat,
  IChatMember,
  IMessage,
  IUser,
} from '@/modules/chat-matrix-fast/types/IChat'

export interface CurrentUserInfo {
  id: ComputedRef<string | null>
  matrixUserId: ComputedRef<string | null>
  name: ComputedRef<string | null>
  nameForChat: ComputedRef<string | null>
  avatar: ComputedRef<string | null>
}

export function useCurrentUser(currentChat?: { members?: IChatMember[] } | null): CurrentUserInfo {
  const userStore = useUserStore()

  const currentUserId = computed(() => {
    const user = userStore.user
    return user?.uuid?.toString() ?? user?.id?.toString() ?? null
  })

  const matrixUserId = computed(() => getCurrentMatrixUserId())

  const currentUserName = computed(() => {
    const user = userStore.user as IUser | null
    if (!user) return null

    const nameFields = [
      user.full_name,
      [user.first_name, user.last_name].filter(Boolean).join(' '),
      [user.last_name, user.first_name].filter(Boolean).join(' '),
      user.user_name,
      user.username,
      user.email,
    ]

    return nameFields.find(field => field && field.trim()) || null
  })

  const currentUserNameForChat = computed(() => {
    if (!currentChat?.members || !currentUserId.value) {
      return currentUserName.value
    }

    const member = currentChat.members.find(
      (m: IChatMember) => m.user === currentUserId.value,
    )

    return member?.user_name || currentUserName.value
  })

  const currentUserAvatar = computed(() => {
    const user = userStore.user as IUser | null
    if (!user) return null

    const avatarFields = [
      user.avatar,
      user.profile_picture,
      user.photo,
      user.image,
    ]

    return avatarFields.find(field => field && field.trim()) || null
  })

  return {
    id: currentUserId,
    matrixUserId,
    name: currentUserName,
    nameForChat: currentUserNameForChat,
    avatar: currentUserAvatar,
  }
}

function normalizeName(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function compareNames(name1: string, name2: string): boolean {
  const norm1 = normalizeName(name1)
  const norm2 = normalizeName(name2)

  if (norm1 === norm2) {
    return true
  }

  const words1 = norm1.split(' ').filter(Boolean)
  const words2 = norm2.split(' ').filter(Boolean)

  if (words1.length === words2.length && words1.length === 2) {
    const reversed1 = `${words1[1]} ${words1[0]}`
    const reversed2 = `${words2[1]} ${words2[0]}`

    return norm1 === reversed2 || norm2 === reversed1
  }

  return false
}

export function isMyMessage(
  message: IMessage,
  currentUserId: string | null,
  currentUserName: string | null,
  currentMatrixUserId?: string | null,
): boolean {
  const matrixUserId = currentMatrixUserId ?? getCurrentMatrixUserId()

  if (!currentUserId && !currentUserName && !matrixUserId) {
    return false
  }

  if (matrixUserId) {
    const matrixSenderIds = [
      message?.author_id,
      message?.created_by?.id,
      typeof message?.author === 'string' ? message.author : null,
    ]
      .filter(id => id !== undefined && id !== null)
      .map(id => String(id))

    if (matrixSenderIds.includes(matrixUserId)) {
      return true
    }
  }

  if (currentUserId) {
    const messageUserIds = [
      message?.author_id,
      message?.user_id,
      message?.created_by?.id,
      typeof message?.author === 'number' ? message.author : null,
      typeof message?.author === 'string' ? message.author : null,
    ]
      .filter(id => id !== undefined && id !== null)
      .map(id => id.toString())

    if (messageUserIds.includes(currentUserId)) {
      return true
    }
  }

  if (currentUserName) {
    const messageUserName
      = message?.author_name
      ?? (typeof message?.author === 'string' ? message.author : null)
      ?? message?.name
      ?? message?.created_by?.full_name
      ?? (message?.created_by?.first_name && message?.created_by?.last_name
        ? `${message.created_by.first_name} ${message.created_by.last_name}`.trim()
        : null)
      ?? (message?.created_by?.last_name && message?.created_by?.first_name
        ? `${message.created_by.last_name} ${message.created_by.first_name}`.trim()
        : null)
      ?? message?.created_by?.user_name
      ?? message?.created_by?.username

    if (messageUserName) {
      return compareNames(messageUserName, currentUserName)
    }
  }

  return false
}
